import { createTheme } from '@mui/material';
import { pink } from '@mui/material/colors';

export const LightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: pink[700],
      dark: pink[800],
      light: pink[500],
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#1f2328',
      dark: '#0d1117',
      light: '#656d76',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f6f8fa',
      paper: '#ffffff'
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    text: {
      primary: '#1f2328',
      secondary: '#656d76',
      disabled: '#8c959f',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    allVariants: {
      color: '#1f2328',
    },
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    subtitle2: {
      fontSize: '0.6875rem',
      fontWeight: 500,
      letterSpacing: '0.04em',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          scrollbarWidth: 'thin',
          scrollbarColor: '#d0d7de #f6f8fa',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f6f8fa',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#d0d7de',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#b1bac4',
            },
          },
        },
        '*': {
          boxSizing: 'border-box',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(236, 64, 122, 0.25)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #ec407a 0%, #d81b60 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #f48fb1 0%, #ec407a 100%)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
        sizeSmall: {
          padding: '6px 14px',
          fontSize: '0.75rem',
          borderRadius: '8px',
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: '1rem',
          borderRadius: '12px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: '16px',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            borderColor: 'rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: '16px',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '20px',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              boxShadow: '0 0 0 3px rgba(236, 64, 122, 0.15)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '9999px',
          fontWeight: 600,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          borderRadius: '8px 8px 0 0',
          '&.Mui-selected': {
            color: pink[700],
          },
          '&:hover': {
            backgroundColor: 'rgba(236, 64, 122, 0.06)',
            borderRadius: '8px 8px 0 0',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: pink[700],
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            fontSize: '1.25rem !important',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '6px',
          },
        },
        sizeSmall: {
          '@media (max-width: 600px)': {
            padding: '4px',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          '@media (max-width: 600px)': {
            fontSize: '1.5rem',
          },
        },
        h2: {
          '@media (max-width: 600px)': {
            fontSize: '1.25rem',
          },
        },
        h3: {
          '@media (max-width: 600px)': {
            fontSize: '1.125rem',
          },
        },
        h4: {
          '@media (max-width: 600px)': {
            fontSize: '1rem',
          },
        },
        h5: {
          '@media (max-width: 600px)': {
            fontSize: '0.875rem',
          },
        },
        h6: {
          '@media (max-width: 600px)': {
            fontSize: '0.8rem',
          },
        },
        body1: {
          '@media (max-width: 600px)': {
            fontSize: '0.8rem',
          },
        },
        body2: {
          '@media (max-width: 600px)': {
            fontSize: '0.75rem',
          },
        },
      },
    },
  },
});
